import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, Bot, TrendingUp, Users2, Crown, ShieldCheck, Sparkles, Globe2, Coins, Map, Rocket, CheckCircle2, Circle, Landmark, Zap, Clock, Layers } from "lucide-react";
import { BrandLockup, BrandMark } from "@/components/BrandLogo";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Feb-21: Lightweight public fetcher (no auth needed for /api/bots and /api/public/config).
async function fetchPublicSnapshot() {
  try {
    const [botsRes, cfgRes] = await Promise.all([
      axios.get(`${API_BASE}/api/bots`),
      axios.get(`${API_BASE}/api/public/config`).catch(() => ({ data: null })),
    ]);
    return { bots: botsRes.data?.bots || [], cfg: cfgRes.data || null };
  } catch {
    return { bots: [], cfg: null };
  }
}

function Nav() {
  return (
    <header className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 flex items-center justify-between gap-2">
      <Link to="/" data-testid="landing-logo" className="flex items-center gap-2 min-w-0 shrink">
        <BrandLockup size={40}/>
      </Link>
      <nav className="hidden md:flex items-center gap-8 text-sm text-slate-300">
        <Link to="/plan" data-testid="landing-nav-plan" className="hover:text-white">Plan</Link>
        <a href="#bots" className="hover:text-white">Bots</a>
        <a href="#ranks" className="hover:text-white">Ranks</a>
        <a href="#about" data-testid="landing-nav-about" className="hover:text-white">About</a>
        <a href="#network" className="hover:text-white">Network</a>
      </nav>
      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        <Link to="/plan" data-testid="landing-plan-mobile" className="md:hidden text-xs sm:text-sm text-slate-200 hover:text-white whitespace-nowrap">Plan</Link>
        <Link to="/login" data-testid="landing-login" className="text-xs sm:text-sm text-slate-200 hover:text-white whitespace-nowrap">Sign in</Link>
        <Link to="/register" data-testid="landing-signup" className="nb-btn-primary text-xs sm:text-sm whitespace-nowrap px-3 sm:px-5"><span className="hidden sm:inline">Get Started</span><span className="sm:hidden">Join</span> <ArrowRight size={14} className="inline ml-1"/></Link>
      </div>
    </header>
  );
}

function Hero({ bots, cfg }) {
  const directPct = cfg?.direct_referral_bonus_pct ?? 5;
  const lvlRaw = cfg?.level_income_pcts;
  const lvl = Array.isArray(lvlRaw) && lvlRaw.length > 0 ? lvlRaw : [10, 5, 3, 2, 1];
  const l6 = cfg?.level_6plus_pct ?? 0.5;
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="max-w-7xl mx-auto px-6 py-20 md:py-28 relative">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1 text-xs text-slate-300 mb-6">
              <Sparkles size={12} className="text-nb-cyan" /> AI Crypto Trading Network · USDT Powered
            </div>
            <h1 className="font-heading font-black text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter text-white">
              Next Vision. <br/>
              <span className="text-nb-cyan">Next Level.</span> <br/>
              <span className="neon-text">Next Billion.</span>
            </h1>
            <p className="mt-8 text-slate-300 text-base md:text-lg max-w-xl">
              A multi-level AI trading network where members activate one of four performance bots, earn daily profit, and grow their team through an unlimited-depth referral engine (Level 1-5: {lvl.map(p=>`${p}%`).join(" / ")} · Level 6+: {l6}% each · unlimited).
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-signup" className="nb-btn-primary text-sm">Join the Network <ArrowRight size={14} className="inline ml-1"/></Link>
              <a href="#bots" className="nb-btn-secondary text-sm">Explore Bots</a>
            </div>
            <div className="mt-12 flex flex-wrap gap-8 text-sm text-slate-400">
              <div><div className="font-mono text-white text-xl">{bots.length || 4}</div>Performance Bots</div>
              <div><div className="font-mono text-white text-xl">∞</div>Team Levels</div>
              <div><div className="font-mono text-white text-xl">${cfg?.min_deposit ?? 10}</div>Minimum Entry</div>
            </div>
          </div>
          <div className="md:col-span-4 hidden md:block">
            <div className="relative flex items-center justify-center">
              <div className="absolute inset-0 blur-3xl opacity-30" style={{background: "radial-gradient(circle, rgba(0,229,255,0.5), transparent 60%)"}}/>
              <BrandMark size={320} withGlow={true} className="relative"/>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Features({ cfg }) {
  const directPct = cfg?.direct_referral_bonus_pct ?? 5;
  const lvlRaw = cfg?.level_income_pcts;
  const lvl = Array.isArray(lvlRaw) && lvlRaw.length > 0 ? lvlRaw : [10, 5, 3, 2, 1];
  const l6 = cfg?.level_6plus_pct ?? 0.5;
  const items = [
    { icon: Bot, title: "4 AI Bots", desc: "Choose from Nova, Pulse, Quantum, Apex — daily profit within company-set ranges." },
    { icon: Users2, title: "Unlimited-Level Team", desc: `Direct referral bonus of ${directPct}% plus multi-level team income — L1-5 (${lvl.map(p=>`${p}%`).join("/")}) and L6+ (${l6}% each) with unlimited depth.` },
    { icon: Crown, title: "Rank System", desc: "Core → Prime → Elite → Supreme with monthly leadership bonuses." },
    { icon: ShieldCheck, title: "USDT Deposits", desc: `TRC20 & BEP20, minimum $${cfg?.min_deposit ?? 10}. Verified withdrawals to your wallet.` },
  ];
  return (
    <section id="network" className="max-w-7xl mx-auto px-6 py-20">
      <div className="grid md:grid-cols-2 gap-10 items-end mb-10">
        <div>
          <div className="text-xs uppercase tracking-widest text-nb-cyan">Ecosystem</div>
          <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tighter mt-2 text-white">Built for network-driven earning</h2>
        </div>
        <p className="text-slate-400">Every layer is engineered for compounding: personal investment yields daily, referrals compound as your downline activates, and leadership pays monthly.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {items.map((f) => (
          <div key={f.title} data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g,'-')}`} className="nb-card p-6 hover:-translate-y-1 hover:border-[#8AE234]/40 transition-all">
            <f.icon className="text-nb-cyan" size={24}/>
            <div className="font-heading font-semibold text-white text-lg mt-4">{f.title}</div>
            <div className="text-sm text-slate-400 mt-2">{f.desc}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CreditLineFacilitySection({ cfg }) {
  const slabsRaw = cfg?.credit_slabs;
  const slabs = Array.isArray(slabsRaw) ? slabsRaw.filter(s => s && s.amount > 0) : [];
  const mult = cfg?.credit_business_multiplier ?? 10;
  const share = cfg?.credit_self_direct_share_pct ?? 50;
  const teamShare = 100 - share;
  const validity = cfg?.credit_validity_days ?? 60;
  const slabDisplay = slabs.length
    ? slabs.map(s => `$${s.amount}`).slice(0, 8).join(" · ")
    : "$100 · $500 · $1,000 · $2,000 · $5,000";

  return (
    <section id="credit" className="relative overflow-hidden py-24">
      <div className="absolute inset-0 opacity-20" style={{background: "radial-gradient(circle at 20% 30%, rgba(234, 179, 8, 0.25), transparent 60%), radial-gradient(circle at 80% 70%, rgba(0, 229, 255, 0.2), transparent 60%)"}}/>
      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#EAB308]/40 px-3 py-1 text-xs text-[#EAB308] uppercase tracking-widest mb-4">
              <Landmark size={12}/> Company Credit Line
            </div>
            <h2 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tighter text-white">
              Trade with the <br/>
              <span className="text-[#EAB308]">Company's</span> capital.
            </h2>
            <p className="mt-6 text-slate-300 text-base leading-relaxed">
              Qualified members can unlock a company-funded credit line — instant activation via email OTP, no personal capital required. Activate any bot from this credit and earn daily profit that sits in your Hold Ledger.
            </p>
            <p className="mt-4 text-slate-400 text-sm">
              Build <span className="text-[#EAB308] font-mono">{mult}× your credit amount</span> in real team business within <span className="text-[#EAB308] font-mono">{validity} days</span> — split <span className="text-nb-cyan font-mono">{share}%</span> self+direct and <span className="text-nb-cyan font-mono">{teamShare}%</span> team — and the entire Hold Profit unlocks instantly to your Main Wallet.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="credit-line-cta" className="nb-btn-primary text-sm">Get Qualified <ArrowRight size={14} className="inline ml-1"/></Link>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/marketing/plan.pdf`} target="_blank" rel="noreferrer" className="nb-btn-secondary text-sm">Read Full Plan PDF</a>
            </div>
          </div>

          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="nb-card p-6 border-[#EAB308]/30" style={{background: "linear-gradient(135deg, rgba(234, 179, 8, 0.10), rgba(0, 0, 0, 0))"}}>
              <Zap size={22} className="text-[#EAB308]"/>
              <div className="mt-3 font-heading font-bold text-white text-lg">Instant Activation</div>
              <p className="mt-2 text-xs text-slate-400 leading-relaxed">Pick a slab, verify with email OTP, credit is live in seconds. No manual approval, no waiting.</p>
            </div>
            <div className="nb-card p-6 border-nb-cyan/30" style={{background: "linear-gradient(135deg, rgba(0, 229, 255, 0.08), rgba(0, 0, 0, 0))"}}>
              <Layers size={22} className="text-nb-cyan"/>
              <div className="mt-3 font-heading font-bold text-white text-lg">Choose your Slab</div>
              <p className="mt-2 text-xs text-slate-400 leading-relaxed">Currently offered: <span className="text-white font-mono">{slabDisplay}</span>. Each slab has its own {mult}× business target.</p>
            </div>
            <div className="nb-card p-6 border-[#22C55E]/30" style={{background: "linear-gradient(135deg, rgba(34, 197, 94, 0.08), rgba(0, 0, 0, 0))"}}>
              <Users2 size={22} className="text-[#22C55E]"/>
              <div className="mt-3 font-heading font-bold text-white text-lg">50 / 50 Business Rule</div>
              <p className="mt-2 text-xs text-slate-400 leading-relaxed">Half from your own + direct referrals, half from your deeper team. Meet both halves within {validity} days.</p>
            </div>
            <div className="nb-card p-6 border-[#A855F7]/30" style={{background: "linear-gradient(135deg, rgba(168, 85, 247, 0.08), rgba(0, 0, 0, 0))"}}>
              <Clock size={22} className="text-[#A855F7]"/>
              <div className="mt-3 font-heading font-bold text-white text-lg">Hold Profit Unlocks</div>
              <p className="mt-2 text-xs text-slate-400 leading-relaxed">Hit the {mult}× target — Hold Profit releases 100% to your Main Wallet. Credit stays usable for the rest of the {validity}-day window.</p>
            </div>
            <div className="sm:col-span-2 rounded-2xl border border-[#EAB308]/20 bg-[#0A0F19] p-6">
              <div className="text-[10px] uppercase tracking-widest text-[#EAB308] mb-2">Key Terms · Read First</div>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-xs text-slate-300 leading-relaxed">
                <li className="flex gap-2"><span className="text-[#EAB308]">•</span> Credit ledger stays fully separate from your Main Wallet.</li>
                <li className="flex gap-2"><span className="text-[#EAB308]">•</span> One-time facility per user · once cleared, cannot re-apply.</li>
                <li className="flex gap-2"><span className="text-[#EAB308]">•</span> Miss the {mult}× target or split ⇒ credit + hold profit expire on day {validity}.</li>
                <li className="flex gap-2"><span className="text-[#EAB308]">•</span> Team commissions & real balance are never touched — completely safe.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


function BotsSection({ bots }) {
  const tierMeta = [
    {tier: "Starter", color: "#22C55E"},
    {tier: "Growth",  color: "#3B82F6"},
    {tier: "Pro",     color: "#A855F7"},
    {tier: "Elite",   color: "#EAB308"},
  ];
  const list = (bots || []).map((b, i) => ({
    name: b.name,
    range: `${b.min_pct}% – ${b.max_pct}%`,
    charge: `${b.service_charge}%`,
    tier: tierMeta[i]?.tier || "",
    color: tierMeta[i]?.color || "#22C55E",
  }));
  return (
    <section id="bots" className="max-w-7xl mx-auto px-6 py-20">
      <div className="text-center mb-14">
        <div className="text-xs uppercase tracking-widest text-nb-cyan">Four AI Bots</div>
        <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tighter mt-2 text-white">Pick your engine</h2>
        <p className="text-slate-400 mt-4 max-w-lg mx-auto">Higher tiers deliver bigger daily returns with higher service charges. Once activated, the bot is locked.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {list.map((b) => (
          <div key={b.name} data-testid={`bot-card-${b.name}`} className="nb-card p-6 group hover:-translate-y-1 transition-transform">
            <div className="text-xs uppercase tracking-widest" style={{color: b.color}}>{b.tier}</div>
            <div className="font-heading font-bold text-white text-xl mt-2">{b.name}</div>
            <div className="mt-6">
              <div className="text-xs text-slate-500">Daily Profit</div>
              <div className="font-mono text-2xl neon-text">{b.range}</div>
            </div>
            <div className="mt-4">
              <div className="text-xs text-slate-500">Service Charge</div>
              <div className="font-mono text-white">{b.charge}</div>
            </div>
            <Link to="/register" data-testid={`bot-cta-${b.name}`} className="mt-6 inline-flex text-sm text-nb-cyan hover:text-white">Get started <ArrowRight size={14} className="ml-1"/></Link>
          </div>
        ))}
      </div>
    </section>
  );
}

function Ranks() {
  const ranks = [
    {name: "Core",    color: "#22C55E", req: "$10 self · Entry tier",           pct: "Starter"},
    {name: "Prime",   color: "#3B82F6", req: "$100 self · Growth tier",         pct: "Growth"},
    {name: "Elite",   color: "#A855F7", req: "$500 self · Advanced tier",       pct: "Advanced"},
    {name: "Supreme", color: "#EAB308", req: "$2K self · Peak tier",            pct: "Elite"},
  ];
  return (
    <section id="ranks" className="max-w-7xl mx-auto px-6 py-20">
      <div className="grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-5">
          <div className="text-xs uppercase tracking-widest text-nb-cyan">Ranks &amp; Leadership</div>
          <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tighter mt-2 text-white">Grow. Rank up. Get paid.</h2>
          <p className="text-slate-400 mt-4">
            Every qualified leader (Active Bot + 10 active directs of $100+ each) earns a
            <span className="text-[#8AE234] font-bold"> unified 10% monthly Leadership Bonus </span>
            on their team&apos;s total trading income — same rate across every rank.
          </p>
        </div>
        <div className="md:col-span-7 grid grid-cols-2 gap-4">
          {ranks.map((r) => (
            <div key={r.name} className="nb-card p-5">
              <div className="text-xs uppercase tracking-widest" style={{color: r.color}}>{r.name}</div>
              <div className="font-heading text-white text-lg mt-2">{r.pct}</div>
              <div className="text-xs text-slate-400 mt-1">{r.req}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutSection() {
  const inspirations = [
    { name: "Cliff Asness",  org: "AQR Capital Management", color: "#8AE234" },
    { name: "David Siegel",  org: "Two Sigma",              color: "#00E5FF" },
    { name: "John Overdeck", org: "Two Sigma",              color: "#A855F7" },
    { name: "Ken Griffin",   org: "Citadel",                color: "#EAB308" },
  ];
  return (
    <section id="about" className="max-w-7xl mx-auto px-6 py-20" data-testid="landing-about">
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="text-xs uppercase tracking-widest text-[#8AE234]">Our Journey</div>
          <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tighter mt-2 text-white">Built on research. Powered by technology.</h2>
          <p className="text-slate-400 mt-4">
            Next Billion.world is a technology-driven Algorithmic Trading Platform built around Market
            Research, Quantitative Analysis, Trading Technology, Data Analysis and Risk Management.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <div className="nb-card p-4 border border-cyan-500/30">
              <div className="text-[10px] uppercase tracking-widest text-cyan-400">Research Started</div>
              <div className="font-heading text-white text-lg mt-1">Jan 15, 2026</div>
            </div>
            <div className="nb-card p-4 border border-[#8AE234]/30">
              <div className="text-[10px] uppercase tracking-widest text-[#8AE234]">International Launch</div>
              <div className="font-heading text-white text-lg mt-1">Jul 28, 2026</div>
            </div>
          </div>
        </div>
        <div className="md:col-span-7 space-y-5">
          <div className="nb-card p-5">
            <div className="text-xs uppercase tracking-widest text-[#EAB308] font-bold">Research Inspiration</div>
            <p className="text-slate-300 text-sm mt-2">
              Our research framework is inspired by prominent figures in modern Quantitative Finance and Systematic Trading:
            </p>
            <div className="mt-3 grid sm:grid-cols-2 gap-2">
              {inspirations.map((p) => (
                <div key={p.name} className="flex items-center gap-2 text-sm">
                  <span className="h-1.5 w-1.5 rounded-full" style={{background: p.color}}/>
                  <span className="text-white font-semibold" style={{color: p.color}}>{p.name}</span>
                  <span className="text-slate-500">·</span>
                  <span className="text-slate-400">{p.org}</span>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-slate-500 italic mt-3">Note: These individuals are not affiliated with Next Billion.world; their contributions serve only as inspiration.</div>
          </div>

          <div className="nb-card p-5 border border-[#EAB308]/30">
            <div className="text-xs uppercase tracking-widest text-[#EAB308] font-bold">Research &amp; Technology Center</div>
            <div className="font-heading text-white mt-1">Sanford Quantitative Trading Research Center (SQTRC)</div>
            <div className="text-slate-400 text-sm mt-1">500 Westover Dr PMB 9816, Sanford, North Carolina 27330, USA</div>
            <div className="text-cyan-300 text-sm mt-1"><a href="mailto:support@nextbillion.world" className="hover:underline">support@nextbillion.world</a></div>
          </div>

          <div className="nb-card p-5 border border-[#8AE234]/40 bg-[#8AE234]/5">
            <div className="text-xs uppercase tracking-widest text-[#8AE234] font-bold">Our Philosophy</div>
            <div className="text-white font-heading text-lg mt-1">Research + Technology + Data + Risk Management</div>
            <div className="text-[#8AE234] font-heading text-lg font-bold">= Intelligent Trading Infrastructure</div>
            <div className="text-slate-400 italic text-xs mt-2">Built on Research. Powered by Technology. Designed for the Future of Trading.</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/5 mt-20">
      <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row gap-6 justify-between items-center text-sm text-slate-400">
        <div className="flex items-center gap-3">
          <BrandMark size={36}/>
          <div>© 2026 Next Billion · <Globe2 size={12} className="inline text-nb-cyan"/> Global Network</div>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500">
          <Link to="/privacy" data-testid="footer-privacy" className="hover:text-white">Privacy Policy</Link>
          <Link to="/terms" data-testid="footer-terms" className="hover:text-white">Terms of Service</Link>
          <Link to="/plan" className="hover:text-white">Plan</Link>
          <Link to="/about" className="hover:text-white">About</Link>
          <a href="mailto:support@nextbillion.world" className="hover:text-white">support@nextbillion.world</a>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  const [bots, setBots] = useState([]);
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const snap = await fetchPublicSnapshot();
      if (!cancelled) { setBots(snap.bots); setCfg(snap.cfg); }
    })();
    return () => { cancelled = true; };
  }, []);
  return (
    <div className="min-h-screen">
      <Nav />
      <Hero bots={bots} cfg={cfg} />
      <Features cfg={cfg} />
      <CreditLineFacilitySection cfg={cfg} />
      <BotsSection bots={bots} />
      <NBTokenSection />
      <RoadmapSection />
      <Ranks />
      <AboutSection />
      <Footer />
    </div>
  );
}

function NBTokenSection() {
  return (
    <section id="nb-token" className="max-w-7xl mx-auto px-6 py-20">
      <div
        className="nb-card p-8 md:p-12 relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgba(234,179,8,0.18) 0%, rgba(15,22,38,1) 55%)",
          borderColor: "rgba(234,179,8,0.35)",
        }}
      >
        <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#EAB308]/20 blur-3xl" />
        <div className="absolute -left-20 -bottom-20 h-64 w-64 rounded-full bg-[#8AE234]/10 blur-3xl" />
        <div className="grid md:grid-cols-2 gap-10 items-center relative">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#EAB308] flex items-center gap-2"><Coins size={12}/> Digital Asset Ecosystem</div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tighter mt-3 text-white">Meet the <span className="text-[#EAB308]">NB Token</span></h2>
            <p className="text-slate-300 mt-4 max-w-lg leading-relaxed">
              A promotional utility token issued to every qualifying investor.
              Locked for the long term. Backed by real platform activity.
              Every deposit and every purchase mints a bond-style certificate — your permanent record of ownership.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-slate-300">
              <li className="flex items-center gap-2"><Sparkles size={14} className="text-[#EAB308]"/> First-deposit allocation for every qualifying investor</li>
              <li className="flex items-center gap-2"><Sparkles size={14} className="text-[#EAB308]"/> Daily mining rewards while your bot is active</li>
              <li className="flex items-center gap-2"><Sparkles size={14} className="text-[#EAB308]"/> Bond-style PDF certificate for every allocation and purchase</li>
              <li className="flex items-center gap-2"><Sparkles size={14} className="text-[#EAB308]"/> Fully isolated wallet — never mixed with USDT</li>
            </ul>
            <Link to="/register" data-testid="landing-nbt-cta" className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full bg-[#EAB308] text-black font-heading font-semibold text-sm hover:opacity-90 transition-opacity">
              Claim Your Allocation <ArrowRight size={14}/>
            </Link>
          </div>
          <div className="flex items-center justify-center">
            <div className="relative">
              <img src="/icons/nb-token-logo.jpg" alt="NBT" className="h-56 w-56 md:h-72 md:w-72 rounded-3xl ring-4 ring-[#EAB308]/30 shadow-2xl" onError={(e) => (e.currentTarget.style.display = "none")}/>
              <div className="absolute -bottom-4 -right-4 bg-black/70 backdrop-blur border border-[#EAB308]/40 rounded-2xl px-4 py-3">
                <div className="text-[10px] uppercase tracking-widest text-[#EAB308]">Symbol</div>
                <div className="font-mono text-white text-2xl">NBT</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const ROADMAP_LANDING = [
  { year: "2027", phase: "🌱 Foundation", subtitle: "Building today for tomorrow", status: "done", color: "#22C55E", items: ["Wallet", "Bonus", "18M Lock", "Reward", "Live Chart"] },
  { year: "2028", phase: "⚡ Utility", subtitle: "Real use · real value", status: "next", color: "#3B82F6", items: ["Buy", "Platform Use", "Partner Ecosystem"] },
  { year: "2029", phase: "🌍 Expansion", subtitle: "Bigger reach · stronger network", status: "planned", color: "#A855F7", items: ["Wider Ecosystem", "External Wallet", "Security"] },
  { year: "2030", phase: "🏆 Global Vision", subtitle: "A token for a brighter tomorrow", status: "planned", color: "#EAB308", items: ["Mass Utility", "Market Readiness", "Global Ecosystem"] },
];

function RoadmapSection() {
  return (
    <section id="roadmap" className="max-w-7xl mx-auto px-6 py-20" data-testid="landing-roadmap">
      <div className="text-center mb-12">
        <div className="text-xs uppercase tracking-widest text-[#EAB308] flex items-center justify-center gap-2"><Rocket size={12}/> NB Token · Vision 2030</div>
        <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tighter mt-3 text-white">More than a token — <span className="text-[#EAB308]">a global movement</span></h2>
        <p className="text-slate-400 mt-4 max-w-2xl mx-auto">A stronger tomorrow for a brighter world. From launch to global ecosystem — every phase adds real utility for holders.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {ROADMAP_LANDING.map((r, i) => (
          <div key={i} className="nb-card p-6 relative overflow-hidden" style={{ borderColor: `${r.color}66` }}>
            <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full blur-3xl" style={{ background: `${r.color}22` }}/>
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono text-4xl font-bold" style={{ color: r.color }}>{r.year}</div>
                {r.status === "done" ? <CheckCircle2 size={22} style={{ color: r.color }}/> : r.status === "next" ? <Rocket size={22} style={{ color: r.color }}/> : <Circle size={22} className="text-slate-500"/>}
              </div>
              <div className="font-heading text-white text-xl">{r.phase}</div>
              <div className="text-[11px] text-slate-400 uppercase tracking-widest mt-1">{r.subtitle}</div>
              <ul className="mt-4 space-y-2 text-sm text-slate-300">
                {r.items.map((it, j) => (
                  <li key={j} className="flex items-center gap-2"><Sparkles size={12} style={{ color: r.color }} className="shrink-0"/> {it}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-8 nb-card p-6 text-center max-w-3xl mx-auto" style={{ borderColor: "rgba(234,179,8,0.25)" }}>
        <div className="font-heading text-white text-xl md:text-2xl flex items-center justify-center gap-3 md:gap-4 flex-wrap">
          <span className="text-[#22C55E]">BUILD</span> →
          <span className="text-[#3B82F6]">UTILIZE</span> →
          <span className="text-[#A855F7]">EXPAND</span> →
          <span className="text-[#EAB308]">GROW</span> 🌐
        </div>
        <p className="text-sm text-slate-300 mt-3">Building a more connected, empowered and inclusive tomorrow.</p>
      </div>
      <div className="text-center mt-6 text-[11px] text-slate-500 italic">Roadmap subject to technology, regulatory and market conditions.</div>
    </section>
  );
}
